// Minimal sanitize utility for sending full component templates to LLM
// - Preserve all configuration fields for context and future extensibility
// - Remove only runtime/ephemeral/heavy fields that should not be sent to LLM
//   (e.g., fetched data snapshots, debug logs, timestamps)

/**
 * Deeply clones a plain JSON-serializable value.
 * Note: We intentionally use JSON clone to drop functions/undefined/cycles,
 *       ensuring the payload is safe to serialize and send to LLM/API.
 */
function deepCloneJson(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    return value;
  }
}

/**
 * Recursively remove keys that should not be sent to LLM.
 * Keep this list minimal and focused on runtime/ephemeral fields only.
 */
const KEYS_TO_REMOVE = new Set([
  'data_result',
  'runtime_state',
  'lastFetchedAt',
  '__internal',
  'debugLogs'
]);

function sanitizeInPlace(node) {
  if (node == null) return;
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      sanitizeInPlace(node[i]);
    }
    return;
  }
  if (typeof node === 'object') {
    Object.keys(node).forEach((key) => {
      if (KEYS_TO_REMOVE.has(key)) {
        delete node[key];
        return;
      }
      sanitizeInPlace(node[key]);
    });
  }
}

/**
 * Create a minimally sanitized clone of a component template.
 * - Keeps all config fields (subscribed_variables, on_click, chart_options, etc.)
 * - Removes only runtime/heavy fields defined in KEYS_TO_REMOVE
 */
export default function sanitizeComponentTemplate(template) {
  if (!template || typeof template !== 'object') return template;
  const cloned = deepCloneJson(template);
  sanitizeInPlace(cloned);
  return cloned;
}


